import { h, type Component, type VNodeChild } from 'vue';
import { RouterLink } from 'vue-router';
import type { FormatterFactory } from '../types';
import { compilePath } from '../gridCell';
import ItpIcon from '../../icon/ItpIcon.vue';

export interface RouteLinkOptions {
  to?: string;
  params?: Record<string, string>;
  innerHTML?: string;
  exportInnerHTML?: boolean;
  navigateOnIconClick?: boolean;
  target?: string;
  icon?: string;
}

const DEFAULT_ICON = 'mdi:open-in-new';

export const routeLink: FormatterFactory = (options: RouteLinkOptions = {}) => {
  const readers = Object.entries(options.params ?? {}).map(
    ([name, path]) => [name, compilePath(path)] as const,
  );

  function paramsFrom(source: any): Record<string, any> | null {
    if (source == null) {
      return null;
    }
    const params: Record<string, any> = {};
    for (const [name, read] of readers) {
      const param = read(source);
      if (param == null) {
        return null;
      }
      params[name] = param;
    }
    return params;
  }

  function routeFor(value: any, context: any): Record<string, any> | null {
    const router = (globalThis as any).$app?.router;
    if (!router || !options.to) {
      return null;
    }
    const params =
      paramsFrom(context) ??
      (value != null && typeof value === 'object' ? paramsFrom(value) : null);
    if (!params) {
      return null;
    }
    const route = { name: `app.${options.to}`, params };
    try {
      const resolved = router.resolve(route);
      return resolved?.matched?.length ? route : null;
    } catch (error) {
      console.error('[ItpDataGrid] routeLink could not resolve', route, error);
      return null;
    }
  }

  const renderer = (
    props: { value: any; context: any },
    { slots }: { slots: Record<string, any> },
  ): VNodeChild => {
    const label = () => [
      options.innerHTML ?? slots.default?.() ?? asText(props.value),
    ];
    const route = routeFor(props.value, props.context);

    if (!route) {
      return h('span', null, label());
    }

    const textLink = h(
      RouterLink,
      {
        to: route,
        target: options.target,
        class: options.navigateOnIconClick ? 'link-dark' : undefined,
      },
      label,
    );

    if (!options.navigateOnIconClick) {
      return textLink;
    }

    return [
      textLink,
      h(
        RouterLink,
        { to: route, target: options.target, class: 'ms-1' },
        () => [h(ItpIcon, { icon: options.icon ?? DEFAULT_ICON })],
      ),
    ];
  };

  renderer.props = ['value', 'context'];

  return {
    component: renderer as Component,
    props: (_value, context) => ({ context }),
  };
};

function asText(value: any): string {
  return value == null ? '' : String(value);
}
